/**
 * Retrieve the value of a browser cookie by name.
 *
 * Returns `null` when the cookie is not present, the name is empty,
 * or `document.cookie` is unavailable (e.g. during SSR).
 */
export function getCookie(name: string): string | null {
  if (!name || typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split("=");
    if (key.trim() === name) {
      const value = rest.join("=").trim();
      return value || null;
    }
  }
  return null;
}

/**
 * Options controlling how a cookie is written.
 */
export interface SetCookieOptions {
  /** Domain the cookie is scoped to, e.g. `designmynight.com`. */
  readonly domain?: string;
  /** Path the cookie applies to. Defaults to `/`. */
  readonly path?: string;
  /** Lifetime in days. When omitted the cookie is a session cookie. */
  readonly days?: number;
  /** SameSite policy. Defaults to `Lax`. */
  readonly sameSite?: "Lax" | "Strict" | "None";
  /** Whether the cookie should only be sent over HTTPS. */
  readonly secure?: boolean;
}

/**
 * Write a browser cookie.
 *
 * By default the cookie is scoped to `path=/` so it is readable across the
 * whole site. Pass `domain` (e.g. `designmynight.com`) to share the cookie
 * across every `*.designmynight.com` subdomain. No-ops during SSR where
 * `document` is unavailable.
 */
export function setCookie(name: string, value: string, options: SetCookieOptions = {}): void {
  if (!name || typeof document === "undefined") return;

  const { domain, path = "/", days, sameSite = "Lax", secure } = options;

  const parts = [`${name}=${value}`, `path=${path}`, `SameSite=${sameSite}`];

  if (domain) parts.push(`domain=${domain}`);

  if (typeof days === "number") {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    parts.push(`expires=${expires.toUTCString()}`);
  }

  if (secure) parts.push("Secure");

  document.cookie = parts.join("; ");
}

/**
 * List the names of every cookie readable from the current document.
 */
export function listCookieNames(): string[] {
  if (typeof document === "undefined") return [];

  return document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name.length > 0);
}

/**
 * Expire a browser cookie.
 *
 * A cookie can only be removed by a write matching its domain and path, so
 * callers that do not know how the cookie was written should call this once per
 * candidate scope. No-ops during SSR.
 */
export function deleteCookie(name: string, options: Pick<SetCookieOptions, "domain" | "path"> = {}): void {
  setCookie(name, "", { ...options, days: -1 });
}

/**
 * Find the value of the first browser cookie whose name ends with the
 * given suffix.  Returns `null` when no match is found or
 * `document.cookie` is unavailable.
 */
export function getCookieBySuffix(suffix: string): string | null {
  if (!suffix || typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split("=");
    if (key.trim().endsWith(suffix)) {
      const value = rest.join("=").trim();
      return value || null;
    }
  }
  return null;
}
